import { useEffect, useRef } from 'react';

import { useDispatch } from 'react-redux';

import { addMinutes, intervalToDuration, differenceInSeconds } from 'date-fns/fp';

import activityDetector, { ACTIVE, IDLE } from '@framework/activityDetector';
import { fetchData } from '@framework/api/apiCalls';
import { api } from '@framework/constants/endpoints';
import {
    TIMER_DURATION,
    countTimeToDisplayTimer,
    DEFAULT_TIME_TO_DISPLAY_TIMER
} from '@framework/constants/session';
import { logoutFreshChat } from '@framework/freshchat';
import { useTranslation } from '@framework/hooks';
import { logout } from '@framework/utils';

import useAuthenticationStatus from '@hooks/useAuthenticationStatus';
import useLoggedUser from '@hooks/useLoggedUser';

import { changeSnackbar, sendSnackbar, dismissSnackbar } from '@redux/reducers/snackbars.duck';

import { SnackbarType } from 'styled-system';

import { resetSessionStorageComplianceConsents } from '@components/layout/Teasers/Teasers.utils';

export const ACTIVITY_DETECTOR_SNACKBAR = 'activity_detector_snackbar';
interface Detector {
    stop?: () => void;
    on?: (event: string, callback: () => void) => void;
}

let detector = {} as Detector;

const useActivityDetector = (): void => {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const timerRef: any = useRef(null);
    const { data: proofStatus } = useAuthenticationStatus();
    const { data: loggedUser, isPending: isLoading } = useLoggedUser({
        enabled: Boolean(proofStatus?.isAuthenticated)
    });

    const prepareNotificationActionTemplate = () => {
        return sendSnackbar({
            id: ACTIVITY_DETECTOR_SNACKBAR,
            message: t("You'll be logged out in {{duration}} minutes.", {
                duration: TIMER_DURATION
            }),
            type: SnackbarType.Warning,
            dismissAllowed: false,
            button: {
                text: t('Extend session'),
                action: () => null
            }
        });
    };

    const handleActivityDetectorStop = () => {
        if (detector?.stop) {
            detector.stop();
        }
    };

    useEffect(() => {
        const { payload, type } = prepareNotificationActionTemplate();

        const handleActivityIdleChange = () => {
            const idleStart = new Date();
            dispatch({ payload, type });
            timerRef.current = setInterval(() => {
                const timeLeft = addMinutes(TIMER_DURATION, idleStart);
                const timeNow = new Date();
                if (differenceInSeconds(timeNow, timeLeft) - 1 < 0) {
                    clearInterval(timerRef?.current);
                    handleActivityDetectorStop();
                    resetSessionStorageComplianceConsents();
                    logoutFreshChat();
                    logout();
                } else {
                    const { minutes, seconds = 0 } = intervalToDuration({
                        start: timeNow,
                        end: timeLeft
                    });
                    dispatch(
                        changeSnackbar({
                            ...payload,
                            id: ACTIVITY_DETECTOR_SNACKBAR,
                            message: t("You'll be logged out in {{duration}} minutes.", {
                                duration: `${minutes}:${seconds / 10 < 1 ? `0${seconds}` : seconds}`
                            })
                        })
                    );
                }
            }, 1000);
        };

        const handleActivityActiveChange = () => {
            // notify server to make sure user is not logged out
            fetchData(api.user()).then(() => {
                dispatch(dismissSnackbar(String(payload.id)));
                dispatch(
                    sendSnackbar({
                        message: t('Your session was extended with success.'),
                        type: SnackbarType.Success
                    })
                );
                clearInterval(timerRef?.current);
            });
        };

        const handleStartActivityDetector = () => {
            const customSessionLen = loggedUser?.sessionInactivityTimeoutMinutes;

            const timeToIdle = customSessionLen
                ? countTimeToDisplayTimer(customSessionLen)
                : DEFAULT_TIME_TO_DISPLAY_TIMER;

            detector = activityDetector({
                timeToIdle
            });

            detector?.on?.(IDLE, handleActivityIdleChange);
            detector?.on?.(ACTIVE, handleActivityActiveChange);
        };

        if (!isLoading && loggedUser) {
            handleStartActivityDetector();
        }

        return () => {
            clearInterval(timerRef?.current);
            handleActivityDetectorStop();
        };
    }, [loggedUser, isLoading]);
};

export default useActivityDetector;
